import { useTranslation } from 'react-i18next';
import React, {useState, useEffect, useRef } from "react";
import Api from "../../services/Api";
import ChatInput from "../ChatInput";
import {CoachInterface} from "../../interfaces/common";
import {ClientInterface} from "../../interfaces/common";
import {ChatInterface} from "../../interfaces/common";
import infoIcon from "url:../../assets/icons/about.svg"
import profileIcon from "url:../../assets/icons/profile.svg";
import {dateFormatSoon} from "../../helpers/dateFunctions";
import WebSocketClient from "../../services/WebSocketClient";
import {getLanguage} from "../../lang/helpers";

interface Props {
  clientId?: string
  coachId?: string
  isCoach: boolean
}

const Chat: React.FC<Props> = ({ clientId, coachId, isCoach }) => {

  const api = Api.getInstance();
  const translation = useTranslation();
  const { i18n } = translation;
  const lang = getLanguage(i18n.language)
  const [chats, _setChats] = useState<Array<ChatInterface>>([])
  const chatsRef = useRef<Array<ChatInterface>>([])
  const [client, setClient] = useState<ClientInterface>()
  const [coach, setCoach] = useState<CoachInterface>()
  const [loaded, setLoaded] = useState<boolean>(false)
  const [activeLicense, setActiveLicense] = useState<boolean>(false)
  const chatWindowRef = useRef<HTMLDivElement>(null)
  const setChats = (newChats: Array<ChatInterface>) => {
    chatsRef.current = newChats;
    _setChats(newChats);
  }

  const webSocketClient = useRef(WebSocketClient.getInstance())

  useEffect(() => {
    const url = clientId ? 'coach/chat/' + clientId + '/get' : 'client/chat/' + coachId + '/get'
    api.get(url).then((result) => {
      const chats: Array<ChatInterface> = []
      for (const chat of result.json.chats as Array<ChatInterface>) {
        chat.dateSent = new Date(chat.dateSent)
        if (chat.dateRead) {
          chat.dateRead = new Date(chat.dateRead)
        }
        chats.push(chat)
      }
      setClient(result.json.client)
      setCoach(result.json.coach)
      setChats(chats)
      setActiveLicense(result.json.activeLicense)
      setLoaded(true)
      webSocketClient.current.webSocket?.addEventListener("message", messageListener)
    })

    return () => {
      webSocketClient.current.webSocket?.removeEventListener("message", messageListener)
    }
  }, [])

  const sendReadAll = () => {
    const url = isCoach ? 'coach/chat/' + clientId + '/read_all' : 'client/chat/' + coachId + '/read_all'
    api.get(url).then(() => console.log('sent read'))
  }

  const messageListener = (event: any) =>  {
    const mess = JSON.parse(event.data)
    const fromId = isCoach ? "client_" + clientId : "coach_" + coachId
    if (mess.chat && mess.fromId === fromId) {
      const newChat: ChatInterface = {
        id: Math.floor(Math.random() * 1000000),
        text: mess.chat.message,
        sentByCoach: false,
        dateSent: new Date(),
        dateRead: new Date(),
      }
      const newChats = [... chatsRef.current]
      newChats.push(newChat)
      setChats(newChats)
      sendReadAll()
    } else {
      console.log('fromId ' + fromId + ' != ' + mess.fromId)
      console.log(mess)
    }

  }

  useEffect(() => {
    if (chats.length > 0 && chatWindowRef.current) {
      chatWindowRef.current.scrollTo(0, chatWindowRef.current.scrollHeight)
    }
  }, [chats])

  if (!loaded) {
    return (<div className="mt-4">Loading chats...</div>)
  }

  const send = (text: string) => {
    if (text.length < 1) {
      return
    }

    const url = isCoach ? 'coach/chat/' + clientId + '/send' : 'client/chat/' + coachId + '/send'
    const data = {
      text: text,
      sentByCoach: isCoach
    }
    api.post(url, data).then((result) => {
      if (result.response.ok) {
        const chat: ChatInterface = {
          id: 0,
          text: text,
          sentByCoach: isCoach,
          dateSent: new Date(),
        }
        if (result.json.readBy && result.json.readBy > 0) {
          chat.dateRead = new Date()
        }
        const newChats = [...chats]
        newChats.push(chat)
        setChats(newChats)
        if (chatWindowRef.current) {
          chatWindowRef.current.scrollTo(0, chatWindowRef.current.scrollHeight)
        }
      }
    })
  }

  const chatInputHeightChange = (height: number) => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTo(0, chatWindowRef.current.scrollHeight)
    }
  }

  return (
    <div className="relative flex justify-between flex-col" style={{height: "calc(100vh - 144px)"}}>
      <div ref={chatWindowRef} className="overflow-y-scroll">
      {
        chats.map((chat: ChatInterface, index) => {
          const img = chat.sentByCoach ? (coach?.picture ? coach.picture : profileIcon)
            :
            (client?.picture ? client.picture : profileIcon)
          const name = chat.sentByCoach ? coach?.firstName + ' ' + coach?.lastName
            :
            client?.firstName + ' ' + client?.lastName
          return (
            <div className="px-4 mt-4 flex" key={"chat_" + index}>
              <img className="h-8 w-8 rounded-full" src={img} alt="pic"/>
              <div className="ml-4">
                <div className="flex">
                  <div className="font-medium">
                    {name}
                  </div>
                  <span className="text-neutral-400 ml-4 font-normal">{dateFormatSoon(chat.dateSent, lang)}</span>
                </div>
                <div className="text-neutral-300 whitespace-pre-line">
                  {chat.text}
                </div>
              </div>
            </div>
          )
        })
      }
      </div>

      <div>
        <hr className="border-neutral-700 mt-4"/>
        {
          activeLicense ? (
            <ChatInput handleSendMessage={send} onHeightChange={chatInputHeightChange} />
          ) : (
            <div className="border-neutral-500 bg-neutral-700 p-4 mt-4 rounded-[6px]">
              <div className="flex items-center">
                <img src={infoIcon} alt="info" className="w-6 h-6" />
                <p className="ml-2">
                  {lang === 'sv' ? 'Coachingperioden har avslutats' : 'Coaching period ended'}
                </p>
              </div>
              <p className="mt-4 text-sm">
                {lang === 'sv' ?
                  'Coachperioden har avslutats och chatten är nu inaktiv. Du kan fortfarande se gamla meddelanden, men du kan inte skicka nya.'
                  :
                  'The coaching period has ended and the chat is now inactive. You can still view your past messages, but new messages can no longer be sent.'
                }
              </p>

            </div>
          )
        }

      </div>


    </div>
  )
}

export default Chat